/**
 * First-run activation wizard API client — typed wrapper over
 * app/api/v1/activation.py:
 *
 *   GET  /activation/progress  — activationApi.getProgress()
 *   PUT  /activation/progress  — activationApi.putProgress(progress)
 *
 * Progress persists under organizations.settings->'activation' (org-scoped),
 * so it survives reloads and auto-satisfies for invitees who land in an org
 * that already has projects. The wizard's activation FUNNEL is tracked
 * separately via recordTelemetry("activation.*").
 */

import { apiClient } from "@/api/client";

export type ActivationStepKey = "project" | "domain" | "team" | "felix";

export interface ActivationStepState {
  done?: boolean;
  skipped?: boolean;
  /** The created project id, once the project step completes. */
  project_id?: string;
}

export interface ActivationProgress {
  dismissed: boolean;
  completed_at: string | null;
  current_step: string | null;
  steps: Partial<Record<ActivationStepKey, ActivationStepState>>;
}

export const EMPTY_ACTIVATION_PROGRESS: ActivationProgress = {
  dismissed: false,
  completed_at: null,
  current_step: null,
  steps: {},
};

export const activationApi = {
  /** GET /activation/progress — the org's wizard progress (empty default). */
  getProgress(): Promise<ActivationProgress> {
    return apiClient.get<ActivationProgress>("/activation/progress");
  },

  /** PUT /activation/progress — persist the wizard progress (merged into
   *  organizations.settings, never replacing other keys). */
  putProgress(progress: ActivationProgress): Promise<ActivationProgress> {
    return apiClient.put<ActivationProgress>("/activation/progress", progress);
  },
};
